import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FactCheck as KontrolisiIcon,
  Lock as LockIcon, // Dodaj Lock ikonu
  ArrowUpward as ArrowUpwardIcon, // Dodaj ikonu za strelicu nagore
  ArrowDownward as ArrowDownwardIcon, // Dodaj ikonu za strelicu naniže
  Refresh as RefreshIcon, // Dodaj Refresh ikonu
  DeleteSweep as DeleteSweepIcon, // Dodaj DeleteSweep ikonu
  PictureAsPdf as PdfIcon,
  Archive as ArchiveIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { ProductMeasurement, PRODUCT_TYPES } from '../types';
import { measurementsApi, lambdaGroupsApi, productsApi, templatesApi, activeSessionsApi } from '../services/api';
import { UserRoleContext } from './Layout';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [measurements, setMeasurements] = useState<ProductMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productTypeFilter, setProductTypeFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lambdaGroups, setLambdaGroups] = useState<{ _id: string; name: string }[]>([]);
  const [selectedLambdaGroup, setSelectedLambdaGroup] = useState<string>('');
  const [products, setProducts] = useState<any[]>([]);
  const [excludeRejected, setExcludeRejected] = useState(false);
  const [excludeVerified, setExcludeVerified] = useState(false);
  const { role } = useContext(UserRoleContext);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // 1. Multi-select state
  const [selectedLambdaGroups, setSelectedLambdaGroups] = useState<string[]>([]);
  const [selectedThicknesses, setSelectedThicknesses] = useState<string[]>([]);
  const [thicknessOptions, setThicknessOptions] = useState<string[]>([]);
  // 1. State za multi-select tip proizvoda
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);

  // 2. Sort state
  const [sortBy, setSortBy] = useState<'datumProizvodnje'>('datumProizvodnje');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 3. Active sessions state
  const [activeSessions, setActiveSessions] = useState<any[]>([]);
  
  // 4. Export state
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const filtersHydratedRef = useRef(false);

  // Ključ za čuvanje filtera u localStorage
  const FILTERS_STORAGE_KEY = 'dashboard_filters_v1';

  // 0. Učitaj prethodne filtere iz localStorage pri prvom mount-u
  useEffect(() => {
    if (filtersHydratedRef.current) return;

    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);

        if (parsed.productTypeFilter !== undefined) setProductTypeFilter(parsed.productTypeFilter);
        if (parsed.startDateFilter !== undefined) setStartDateFilter(parsed.startDateFilter);
        if (parsed.endDateFilter !== undefined) setEndDateFilter(parsed.endDateFilter);
        if (Array.isArray(parsed.selectedLambdaGroups)) setSelectedLambdaGroups(parsed.selectedLambdaGroups);
        if (Array.isArray(parsed.selectedProductTypes)) setSelectedProductTypes(parsed.selectedProductTypes);
        if (Array.isArray(parsed.selectedThicknesses)) setSelectedThicknesses(parsed.selectedThicknesses);
        if (typeof parsed.excludeRejected === 'boolean') setExcludeRejected(parsed.excludeRejected);
        if (typeof parsed.excludeVerified === 'boolean') setExcludeVerified(parsed.excludeVerified);
        if (typeof parsed.page === 'number') setPage(parsed.page);
        if (typeof parsed.rowsPerPage === 'number') setRowsPerPage(parsed.rowsPerPage);
        if (parsed.sortBy) setSortBy(parsed.sortBy);
        if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
      }
    } catch {
      // Ignoriši grešku pri čitanju/parsiranju
    }

    filtersHydratedRef.current = true;
  }, []);

  // 0b. Sačuvaj filtere svaki put kada se promene
  useEffect(() => {
    if (!filtersHydratedRef.current) return;

    const payload = {
      productTypeFilter,
      startDateFilter,
      endDateFilter,
      selectedLambdaGroups,
      selectedProductTypes,
      selectedThicknesses,
      excludeRejected,
      excludeVerified,
      page,
      rowsPerPage,
      sortBy,
      sortOrder,
    };

    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignoriši grešku pri upisu
    }
  }, [
    productTypeFilter,
    startDateFilter,
    endDateFilter,
    selectedLambdaGroups,
    selectedProductTypes,
    selectedThicknesses,
    excludeRejected,
    excludeVerified,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    // console.log('🔍 Dashboard useEffect - loading initial data at:', new Date().toISOString());
    loadMeasurements();
    lambdaGroupsApi.getAll().then(setLambdaGroups);
    productsApi.getAll().then(setProducts);
    
    // Učitaj aktivne sesije samo ako je korisnik admin
    if (currentUser.role === 'admin' || currentUser.role === 'master_admin' || currentUser.role === 'direktor') {
      loadActiveSessions();
    }
    

  }, [currentUser.role]);

  // Automatsko osvežavanje aktivnih sesija svakih 1 sekund (samo za admine)
  useEffect(() => {
    // Proveri da li je korisnik admin
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin' && currentUser.role !== 'direktor') {
      // console.log('🔍 User is not admin, skipping active sessions refresh');
      return;
    }
    
    // console.log('🔍 Setting up active sessions refresh interval (1s) for admin user');
    const interval = setInterval(() => {
      // console.log('🔍 Auto-refreshing active sessions at:', new Date().toISOString());
      loadActiveSessions();
    }, 1000);
    return () => {
      // console.log('🔍 Clearing active sessions refresh interval');
      clearInterval(interval);
    };
  }, [currentUser.role]);

  const loadActiveSessions = async () => {
    // Proveri da li je korisnik admin
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      // console.log('🔍 User is not admin, skipping active sessions load');
      return;
    }
    
    try {
      const sessions = await activeSessionsApi.list();

      // Filtriraj samo stvarno aktivne sesije (nije starije od 5 minuta)
      const now = Date.now();
      const onlyActive = sessions.filter((session: any) => {
        const sessionAge = now - new Date(session.lastActivity).getTime();
        return sessionAge <= 5 * 60 * 1000;
      });

      setActiveSessions(onlyActive);
    } catch {
      // Ako token istekne, axios interceptor će uraditi refresh
      // i eventualno preusmeriti na login; ovde ne moramo ništa.
    }
  };

  // 3. Update thickness options when lambda group changes
  useEffect(() => {
    if (selectedLambdaGroups.length > 0) {
      const filtered = products.filter((p: any) => selectedLambdaGroups.includes(p.lambdaGroup));
      const thicknesses = Array.from(new Set(filtered.map((p: any) => String(p.thickness)))).map(String);
      setThicknessOptions(thicknesses);
    } else {
      setThicknessOptions([]);
    }
    setSelectedThicknesses([]);
  }, [selectedLambdaGroups, products]);

  const loadMeasurements = async () => {
    // console.log('🔍 loadMeasurements called at:', new Date().toISOString());
    try {
      setLoading(true);
      setError(null);
      const data = await measurementsApi.getAll();
      // console.log('🔍 Measurements loaded:', data.length, 'measurements');
      setMeasurements(data);
    } catch (err) {
      // console.error('🔍 Error loading measurements:', err);
      setError('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };



  const handleFilter = async () => {
    // Filtriranje se sada radi frontend-om kroz filteredMeasurements
    // Resetuj paginaciju na prvu stranicu kada se primeni filter
    setPage(0);
  };

  const clearFilters = () => {
    // Očisti sve filtere
    setProductTypeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSelectedLambdaGroups([]);
    setSelectedProductTypes([]);
    setSelectedThicknesses([]);
    setExcludeRejected(false);
    setExcludeVerified(false);
    setPage(0);
    
    // Resetuj na sva merenja
    loadMeasurements();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovo merenje?')) {
      try {
        await measurementsApi.delete(id);
        setMeasurements(measurements.filter(m => m._id !== id));
      } catch (err) {
        setError('Greška pri brisanju merenja');
      }
    }
  };

  // 4. Filtering logic
  const filteredMeasurements = measurements.filter(measurement => {
    // Lambda grupa filter (multi-select)
    if (selectedLambdaGroups.length > 0) {
      const product = products.find((p) => p.name === measurement.productType);
      if (!product || !selectedLambdaGroups.includes(product.lambdaGroup)) return false;
    }
    
    // Tip proizvoda filter (multi-select)
    if (selectedProductTypes.length > 0 && !selectedProductTypes.includes(measurement.productType)) return false;
    
    // Stari tip proizvoda filter (single select - za kompatibilnost)
    if (productTypeFilter && measurement.productType !== productTypeFilter) return false;
    
    // Debljina filter (multi-select)
    if (selectedThicknesses.length > 0) {
      const product = products.find((p) => p.name === measurement.productType);
      if (!product || !selectedThicknesses.includes(String(product.thickness))) return false;
    }
    
    // Datum filteri
    if (startDateFilter && measurement.datumProizvodnje < startDateFilter) return false;
    if (endDateFilter && measurement.datumProizvodnje > endDateFilter) return false;
    
    // Status filteri
    if (excludeRejected && measurement.notVerified) return false;
    if (excludeVerified && measurement.verified) return false;
    
    return true;
  });

  // 5. Sorting logic
  const sortedMeasurements = sortBy
    ? [...filteredMeasurements].sort((a, b) => {
        if (sortBy === 'datumProizvodnje') {
          const aDate = new Date(a.datumProizvodnje).getTime();
          const bDate = new Date(b.datumProizvodnje).getTime();
          return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
        }
        return 0;
      })
    : filteredMeasurements;

  // Filtrirani tipovi proizvoda na osnovu izabrane lambda grupe
  const filteredProductTypes = selectedLambdaGroup
    ? products.filter((p) => p.lambdaGroup === selectedLambdaGroup).map((p) => p.name)
    : products.map((p) => p.name);

  // Paginacija
  const paginatedMeasurements = sortedMeasurements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Dodaj handler za dupliranje
  const cleanupDuplicateSessions = async () => {
    // Proveri da li je korisnik admin
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      // console.error('🔍 Non-admin user trying to cleanup sessions');
      alert('Samo admin može da čisti sesije');
      return;
    }
    
    try {
      await activeSessionsApi.cleanupDuplicates();
      await loadActiveSessions();
    } catch {
      // Ignoriši grešku; najčešće je vezana za auth/token
    }
  };

  const handleTakeOver = async (sessionId: string) => {
    // Proveri da li je korisnik admin
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      // console.error('🔍 Non-admin user trying to take over session');
      alert('Samo admin može da preuzme sesiju');
      return;
    }
    
    // console.log('🔍 handleTakeOver called with sessionId:', sessionId, 'at:', new Date().toISOString());
    
    if (!sessionId) {
      // console.error('🔍 No sessionId provided');
      alert('Greška: Session ID nije dostupan');
      return;
    }

    // Proveri da li je sesija stvarno aktivna pre take over-a
    const session = activeSessions.find(s => s.sessionId === sessionId);
    if (session) {
      const sessionAge = Date.now() - new Date(session.lastActivity).getTime();
      if (sessionAge > 5 * 60 * 1000) { // 5 minuta
        // console.log('🔍 Session expired before take over, sessionId:', sessionId);
        alert('Sesija je istekla i ne može se preuzeti');
        // Osveži sesije da se ukloni istekla sesija
        await loadActiveSessions();
        return;
      }
    }

    try {
      // console.log('🔍 Take over request for sessionId:', sessionId);
      // console.log('🔍 Current activeSessions before take over:', activeSessions);
      // console.log('🔍 Making fetch request to /api/active-sessions/take-over');
      
      const data = await activeSessionsApi.takeOver(sessionId);
        
        // Pronađi merenje koje je preuzeto
        const session = activeSessions.find(s => s.sessionId === sessionId);
        if (session && session.resourceId) {
          // console.log('🔍 Found session for take over, navigating to edit:', session.resourceId);
          // Prikaži poruku o uspešnom preuzimanju
          alert(`Admin ${data.session.takenBy} je preuzeo kontrolu nad merenjem od ${data.session.previousEditor}. Preusmeravanje na editovanje...`);
          // Preusmeri admina na editovanje merenja
          setTimeout(() => {
            window.location.href = `/edit/${session.resourceId}`;
          }, 1000); // Kratka pauza da korisnik vidi poruku
        } else {
          alert(`Admin ${data.session.takenBy} je preuzeo kontrolu nad merenjem od ${data.session.previousEditor}`);
          // Samo osveži sesije, ne merenja
          // console.log('🔍 Reloading active sessions after take over...');
          await loadActiveSessions();
          // console.log('🔍 Active sessions reloaded after take over');
        }
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Greška pri preuzimanju sesije';
      alert(`Greška: ${msg}`);
    }
  };

  const handleDuplicate = async (measurement: ProductMeasurement) => {
    // Ukloni status polja i generiši novi objekat
    const { _id, verified, notVerified, verifiedBy, notVerifiedBy, createdAt, ...rest } = measurement;
    const newMeasurement = {
      ...rest,
      verified: false,
      notVerified: false,
      verifiedBy: '',
      notVerifiedBy: '',
      secena: measurement.secena ?? '', // ProductFormData zahteva secena: string
      naknadno: true, // Dodaj oznaku za duplikat
    };
    try {
      setLoading(true);
      setError(null);
      await measurementsApi.create(newMeasurement);
      await loadMeasurements();
    } catch (err) {
      setError('Greška pri dupliranju merenja');
    } finally {
      setLoading(false);
    }
  };

  // Pronađi sve duplikate po productType i datumProizvodnje
  const duplicateMap = new Map<string, number>();
  measurements.forEach(m => {
    const key = `${m.productType}__${m.datumProizvodnje}`;
    duplicateMap.set(key, (duplicateMap.get(key) || 0) + 1);
  });
  const isMeasurementBeingEdited = (measurementId: string) => {
    // Ako korisnik nije admin, ne može da vidi aktivne sesije
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      return false;
    }
    
    // console.log('🔍 Checking if measurement is being edited:', measurementId, 'activeSessions count:', activeSessions.length);
    // console.log('🔍 Active sessions:', activeSessions.map(s => ({ resourceId: s.resourceId, status: s.status, lastActivity: s.lastActivity })));
    
    const isEdited = activeSessions.some(session => 
      session.resourceId === measurementId && 
      session.resourceType === 'measurement' &&
      session.status === 'active' &&
      // Proveri da li je sesija stvarno aktivna (nije starija od 5 minuta - smanjeno sa 10)
      new Date(session.lastActivity).getTime() > Date.now() - 5 * 60 * 1000 &&
      // Proveri da li trenutni korisnik NIJE vlasnik sesije (za admin/master_admin)
      !(session.username === currentUser.username && (role === 'admin' || role === 'master_admin'))
    );
    
    if (isEdited) {
      // console.log('🔍 Measurement is being edited:', measurementId);
    } else {
      // Dodatna provera - možda je sesija u bazi ali nije aktivna
      const staleSession = activeSessions.find(session => 
        session.resourceId === measurementId && 
        session.resourceType === 'measurement' &&
        session.status === 'active' &&
        new Date(session.lastActivity).getTime() <= Date.now() - 5 * 60 * 1000
      );
      if (staleSession) {
        // console.log('🔍 Found stale session for measurement:', measurementId, 'lastActivity:', staleSession.lastActivity);
      }
      
      // Proveri sve sesije za ovaj measurement
      const allSessionsForMeasurement = activeSessions.filter(session => 
        session.resourceId === measurementId && 
        session.resourceType === 'measurement'
      );
      if (allSessionsForMeasurement.length > 0) {
        // console.log('🔍 All sessions for measurement:', measurementId, allSessionsForMeasurement);
      }
    }
    
    return isEdited;
  };

  const getActiveSession = (measurementId: string) => {
    // Ako korisnik nije admin, ne može da vidi aktivne sesije
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      return null;
    }
    
    // console.log('🔍 getActiveSession called for measurementId:', measurementId, 'activeSessions count:', activeSessions.length);
    // console.log('🔍 Active sessions in getActiveSession:', activeSessions.map(s => ({ resourceId: s.resourceId, status: s.status, lastActivity: s.lastActivity })));
    
    const session = activeSessions.find(session => 
      session.resourceId === measurementId && 
      session.resourceType === 'measurement' &&
      session.status === 'active' &&
      // Proveri da li je sesija stvarno aktivna (nije starija od 5 minuta - smanjeno sa 10)
      new Date(session.lastActivity).getTime() > Date.now() - 5 * 60 * 1000
    );
    
    // console.log('🔍 getActiveSession for measurementId:', measurementId, 'found session:', session);
    if (session) {
      // console.log('🔍 Session owner:', session.username, 'current user:', currentUser.username, 'is owner:', session.username === currentUser.username);
    }
    
    // console.log('🔍 getActiveSession for measurementId:', measurementId, 'found session:', session);
    
    if (!session) {
      // Proveri sve sesije za ovaj measurement
      const allSessionsForMeasurement = activeSessions.filter(s => 
        s.resourceId === measurementId && 
        s.resourceType === 'measurement'
      );
      if (allSessionsForMeasurement.length > 0) {
        // console.log('🔍 No active session found, but found other sessions:', allSessionsForMeasurement);
      }
    }
    
    return session;
  };

  const isDuplicate = (m: ProductMeasurement, idx: number, arr: ProductMeasurement[]) => {
    const key = `${m.productType}__${m.datumProizvodnje}`;
    // Pronađi sve sa istim ključem
    const all = arr.filter(x => `${x.productType}__${x.datumProizvodnje}` === key);
    // Ako ih ima više, svi osim prvog su duplikati
    if (all.length > 1) {
      const firstId = all[0]._id;
      return m._id !== firstId;
    }
    return false;
  };

  // Funkcija koja proverava da li je trenutni korisnik vlasnik sesije za merenje
  const isCurrentUserEditingMeasurement = (measurementId: string) => {
    // Ako korisnik nije admin, ne može da vidi aktivne sesije
    if (currentUser.role !== 'admin' && currentUser.role !== 'master_admin') {
      return false;
    }
    
    const session = getActiveSession(measurementId);
    return session && session.username === currentUser.username;
  };

  // Export functions
  const generateProductFormPDF = async (measurement: ProductMeasurement): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 35; // Increased for logo space
    
    // Load template data for validation
    const product = products.find(p => p.name === measurement.productType);
    let template: any = null;
    if (product) {
      try {
        template = await templatesApi.getByProductId(product._id);
      } catch (error) {
        // console.log('Could not load template for product:', product.name);
      }
    }

    // Helper functions
    const formatNumber = (value: number | string | undefined | null): string => {
      if (value === null || value === undefined || value === '') return '';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(num) ? '' : num.toFixed(2);
    };

    const formatNumberOrEmpty = (value: number | string | undefined | null): string => {
      if (value === null || value === undefined || value === '') return '';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(num) ? '' : num.toFixed(2);
    };

    const getValidationColor = (value: string, field: string, template: any): string => {
      if (!value || value === '' || !template) return '#f8f9fa'; // default light gray
      
      const num = parseFloat(value);
      if (isNaN(num)) return '#f8f9fa';
      
      switch (field) {
        case 'gustina':
          if (template.densityRange && template.densityRange.min && template.densityRange.max) {
            return (num >= template.densityRange.min && num <= template.densityRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'procenatSM':
          if (template.smRange && template.smRange.min && template.smRange.max) {
            return (num >= template.smRange.min && num <= template.smRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'avgAFr':
          if (template.afrRange && template.afrRange.min) {
            return (num >= template.afrRange.min) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'avgDuzina':
          if (template.lengthRange && template.lengthRange.min && template.lengthRange.max) {
            return (num >= template.lengthRange.min && num <= template.lengthRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'avgSirina':
          if (template.widthRange && template.widthRange.min && template.widthRange.max) {
            return (num >= template.widthRange.min && num <= template.widthRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'avgThickness':
          if (template.thicknessRange && template.thicknessRange.min && template.thicknessRange.max) {
            return (num >= template.thicknessRange.min && num <= template.thicknessRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'avgDistribucija':
          if (template.fiberDistRange && template.fiberDistRange.min && template.fiberDistRange.max) {
            return (num >= template.fiberDistRange.min && num <= template.fiberDistRange.max) ? '#c8e6c9' : '#ffcdd2';
          }
          break;
        case 'lambdaVrednost':
          // Lambda vrednost se validira na osnovu tolerancije ako postoji referentna vrednost
          if (template.lambdaTolerance && measurement.lambdaVrednost) {
            // Ako imamo referentnu Lambda vrednost u template-u
            if (template.referenceLambda) {
              const diff = Math.abs(num - template.referenceLambda);
              return diff <= template.lambdaTolerance ? '#c8e6c9' : '#ffcdd2';
            }
          }
          break;
        case 'cs10':
          // CS se validira na osnovu tolerancije ako postoji referentna vrednost
          if (template.csTolerance && measurement.cs10) {
            // Ako imamo referentnu CS vrednost u template-u
            if (template.referenceCS) {
              const diff = Math.abs(num - template.referenceCS);
              return diff <= template.csTolerance ? '#c8e6c9' : '#ffcdd2';
            }
          }
          break;
      }
      return '#f8f9fa'; // default
    };

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 248, g: 249, b: 250 }; // default light gray
    };

    const drawBox = (x: number, y: number, width: number, height: number, fillColor?: string) => {
      if (fillColor) {
        const rgb = hexToRgb(fillColor);
        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.rect(x, y, width, height, 'F');
      }
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(x, y, width, height);
    };

    const drawField = (label: string, value: string, x: number, y: number, width: number = 50, height: number = 8) => {
      // Label
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, x + 1, y - 1);
      
      // Value box
      drawBox(x, y, width, height, '#f8f9fa');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(value, x + 2, y + 5.5);
    };

    const drawValidatedField = (label: string, value: string, x: number, y: number, width: number = 50, height: number = 8, fieldType: string, template: any) => {
      // Label
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, x + 1, y - 1);
      
      // Get validation color
      const backgroundColor = getValidationColor(value, fieldType, template);
      
      // Value box
      drawBox(x, y, width, height, backgroundColor);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(value, x + 2, y + 5.5);
      
      // Add range info below the field
      const rangeText = getRangeText(fieldType, template);
      if (rangeText) {
        pdf.setFontSize(6);
        pdf.setTextColor(100, 100, 100);
        pdf.text(rangeText, x + 2, y + height + 3);
        pdf.setTextColor(0, 0, 0);
      }
    };

    const getRangeText = (fieldType: string, template: any): string => {
      if (!template) return '';
      
      switch (fieldType) {
        case 'gustina':
          if (template.densityRange && template.densityRange.min && template.densityRange.max) {
            return `Min: ${template.densityRange.min} - Max: ${template.densityRange.max}`;
          }
          break;
        case 'procenatSM':
          if (template.smRange && template.smRange.min && template.smRange.max) {
            return `Min: ${template.smRange.min}% - Max: ${template.smRange.max}%`;
          }
          break;
        case 'avgAFr':
          if (template.afrRange && template.afrRange.min) {
            return `Min: ${template.afrRange.min} kPa·s/m²`;
          }
          break;
        case 'avgDuzina':
          if (template.lengthRange && template.lengthRange.min && template.lengthRange.max) {
            return `Min: ${template.lengthRange.min} - Max: ${template.lengthRange.max} mm`;
          }
          break;
        case 'avgSirina':
          if (template.widthRange && template.widthRange.min && template.widthRange.max) {
            return `Min: ${template.widthRange.min} - Max: ${template.widthRange.max} mm`;
          }
          break;
        case 'avgThickness':
          if (template.thicknessRange && template.thicknessRange.min && template.thicknessRange.max) {
            return `Min: ${template.thicknessRange.min} - Max: ${template.thicknessRange.max} mm`;
          }
          break;
        case 'avgDistribucija':
          if (template.fiberDistRange && template.fiberDistRange.min && template.fiberDistRange.max) {
            return `Min: ${template.fiberDistRange.min} - Max: ${template.fiberDistRange.max} mm`;
          }
          break;
        case 'lambdaVrednost':
          if (template.lambdaTolerance) {
            return `Tolerancija: ±${template.lambdaTolerance}`;
          }
          break;
        case 'cs10':
          if (template.csTolerance) {
            return `Tolerancija: ±${template.csTolerance}`;
          }
          break;
      }
      return '';
    };

    const drawSectionTitle = (title: string, x: number, y: number, width: number) => {
      // Section title bez okvira
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text(title, x, y);
      
      // Podvuci naslov sa tankom linijom
      pdf.setDrawColor(120, 120, 120);
      pdf.setLineWidth(0.3);
      pdf.line(x, y + 2, x + width, y + 2);
      
      pdf.setTextColor(0, 0, 0); // Vrati boju na crnu
      return y + 8; // Return position for content (manji razmak)
    };

    const drawSimpleField = (label: string, value: string, x: number, y: number, width: number = 50) => {
      // Label
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(80, 80, 80);
      pdf.text(label, x, y);
      
      // Value
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(value || '', x, y + 6);
    };

    const drawValidatedSimpleField = (label: string, value: string, x: number, y: number, width: number = 50, fieldType: string, template: any) => {
      // Label
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(80, 80, 80);
      pdf.text(label, x, y);
      
      // Get validation color
      const backgroundColor = getValidationColor(value, fieldType, template);
      
      // Draw subtle background za validated polja
      if (backgroundColor !== '#f8f9fa') {
        const rgb = hexToRgb(backgroundColor);
        pdf.setFillColor(rgb.r + 20, rgb.g + 20, rgb.b + 20); // Svetlija verzija
        pdf.rect(x - 1, y + 2, width, 6, 'F');
      }
      
      // Value
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(value || '', x, y + 6);
      
      // Range info
      const rangeText = getRangeText(fieldType, template);
      if (rangeText) {
        pdf.setFontSize(6);
        pdf.setTextColor(120, 120, 120);
        pdf.text(rangeText, x, y + 12);
        pdf.setTextColor(0, 0, 0);
      }
    };

        // Header with logo area and title
    pdf.setFillColor(0, 102, 204);
    pdf.rect(0, 0, pageWidth, 25, 'F');
    
    // // Logo area (placeholder - you can add actual logo later)
    // pdf.setFillColor(255, 255, 255);
    // pdf.rect(10, 5, 15, 15, 'F');
    // pdf.setTextColor(0, 0, 0);
    // pdf.setFontSize(8);
    // pdf.setFont('helvetica', 'bold');
    // pdf.text('LOGO', 17.5, 13, { align: 'center' });
    
    // // Company info
    // pdf.setTextColor(255, 255, 255);
    // pdf.setFontSize(10);
    // pdf.text('THERMOWOOL', 30, 10);
    // pdf.setFontSize(8);
    // pdf.text('Tehnička merenja proizvoda', 30, 15);
    
    // Title - better fitted
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('IZVESTAJ ZA TEHNICKA MERENJA', pageWidth/2, 12, { align: 'center' });
    pdf.setFontSize(12);
    pdf.text('PROIZVODA', pageWidth/2, 18, { align: 'center' });
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');

    // Basic Information Section
    const osnovniPodaciSectionHeight = 60; // 2 reda po 3 polja + header + padding
    yPosition = drawSectionTitle('OSNOVNI PODACI', 15, yPosition, pageWidth - 30);
    
    drawSimpleField('Tip proizvoda:', measurement.productType, 20, yPosition, 80);
    drawSimpleField('Smena:', measurement.smena || '', 110, yPosition, 40);
    yPosition += 12;
    
    drawSimpleField('Datum proizvodnje:', format(new Date(measurement.datumProizvodnje), 'dd.MM.yyyy'), 20, yPosition, 50);
    drawSimpleField('Vreme proizvodnje:', measurement.vremeProizvodnje || '', 80, yPosition, 50);
    drawSimpleField('Kontrolisao:', measurement.kontrolisao, 140, yPosition, 50);
    yPosition += 20;

    // Gustina Section - uvek prikaži sekciju
    const gustinaSectionHeight = 55; // 3 polja + header + padding
    
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - gustinaSectionHeight - 20) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('GUSTINA', 15, yPosition, pageWidth - 30);
    
    drawSimpleField('Masa rolne (kg):', formatNumber(measurement.masaRolne), 20, yPosition, 50);
    drawSimpleField('Zapremina (m³):', formatNumber(measurement.zapremina), 80, yPosition, 50);
    drawValidatedSimpleField('Gustina (kg/m³):', formatNumber(measurement.gustina), 140, yPosition, 50, 'gustina', template);
    yPosition += 25;

    // Suva materija Section - uvek prikaži sekciju
    const suvaMaterijaSectionHeight = 65; // 4 polja + SM polje + header + padding
    
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - suvaMaterijaSectionHeight - 20) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('SUVA MATERIJA', 15, yPosition, pageWidth - 30);
    
    drawSimpleField('m1 (g):', formatNumber(measurement.m1), 20, yPosition, 35);
    drawSimpleField('m2 (g):', formatNumber(measurement.m2), 60, yPosition, 35);
    drawSimpleField('m3 (g):', formatNumber(measurement.m3), 100, yPosition, 35);
    drawSimpleField('m4 (g):', formatNumber(measurement.m4), 140, yPosition, 35);
    yPosition += 12;
    
    drawValidatedSimpleField('SM (%):', formatNumber(measurement.procenatSM), 20, yPosition, 60, 'procenatSM', template);
    yPosition += 25;

    // Izmerene vrednosti dužine - dinamički prikaz
    if (measurement.duzineRows && measurement.duzineRows.length > 0) {
      // Kalkuliši tačnu visinu na osnovu broja polja
      const rowsNeeded = Math.ceil(measurement.duzineRows.length / 3); // 3 kolone po redu
      const fieldsHeight = rowsNeeded * 12; // Svaki red polja je 12px
      const avgFieldHeight = 25; // Prostor za srednju vrednost
      const sectionPadding = 20; // Header i padding
      const totalSectionHeight = fieldsHeight + avgFieldHeight + sectionPadding;
      
      // Proveri da li cela sekcija stane na stranicu
      if (yPosition > pageHeight - totalSectionHeight - 20) {
        pdf.addPage();
        yPosition = 25;
      }
      
      yPosition = drawSectionTitle('IZMERENE VREDNOSTI DUŽINE (mm)', 15, yPosition, pageWidth - 30);
      
      // Prikaži sve dinamičke dužine
      measurement.duzineRows.forEach((row, index) => {
        const xPos = 20 + (index % 3) * 60; // 3 kolone
        if (index % 3 === 0 && index > 0) yPosition += 12; // Nova linija svake 3 polja
        
        drawSimpleField(`Dužina na ${row.duzina} mm:`, formatNumber(row.izmerena), xPos, yPosition, 55);
      });
      
      if (measurement.duzineRows.length > 0) yPosition += 12;
      drawValidatedSimpleField('Srednja vrednost (mm):', formatNumber(measurement.avgDuzina), 20, yPosition, 80, 'avgDuzina', template);
      yPosition += 25;
    } else {
      // Fallback na stare fiksne vrednosti ako nema duzineRows
      const fallbackSectionHeight = 65; // 3 polja + srednja vrednost + header
      
      // Proveri da li cela sekcija stane na stranicu
      if (yPosition > pageHeight - fallbackSectionHeight - 20) {
        pdf.addPage();
        yPosition = 25;
      }
      yPosition = drawSectionTitle('IZMERENE VREDNOSTI DUŽINE (mm)', 15, yPosition, pageWidth - 30);
      
      drawSimpleField('Dužina na 200 mm:', formatNumber(measurement.duzina200), 20, yPosition, 50);
      drawSimpleField('Dužina na 600 mm:', formatNumber(measurement.duzina600), 80, yPosition, 50);
      drawSimpleField('Dužina na 1000 mm:', formatNumber(measurement.duzina1000), 140, yPosition, 50);
      yPosition += 12;
      
      drawValidatedSimpleField('Srednja vrednost (mm):', formatNumber(measurement.avgDuzina), 20, yPosition, 80, 'avgDuzina', template);
      yPosition += 25;
    }

    // Izmerene vrednosti širine - dinamički prikaz
    if (measurement.sirineRows && measurement.sirineRows.length > 0) {
      // Kalkuliši tačnu visinu na osnovu broja polja
      const rowsNeeded = Math.ceil(measurement.sirineRows.length / 3); // 3 kolone po redu
      const fieldsHeight = rowsNeeded * 12; // Svaki red polja je 12px
      const avgFieldHeight = 25; // Prostor za srednju vrednost
      const sectionPadding = 20; // Header i padding
      const totalSectionHeight = fieldsHeight + avgFieldHeight + sectionPadding;
      
      // Proveri da li cela sekcija stane na stranicu
      if (yPosition > pageHeight - totalSectionHeight - 20) {
        pdf.addPage();
        yPosition = 25;
      }
      
      yPosition = drawSectionTitle('IZMERENE VREDNOSTI ŠIRINE (mm)', 15, yPosition, pageWidth - 30);
      
      // Prikaži sve dinamičke širine
      measurement.sirineRows.forEach((row, index) => {
        const xPos = 20 + (index % 3) * 60; // 3 kolone
        if (index % 3 === 0 && index > 0) yPosition += 12; // Nova linija svake 3 polja
        
        drawSimpleField(`Širina na ${row.sirina} mm:`, formatNumber(row.izmerena), xPos, yPosition, 55);
      });
      
      if (measurement.sirineRows.length > 0) yPosition += 12;
      drawValidatedSimpleField('Srednja vrednost (mm):', formatNumber(measurement.avgSirina), 20, yPosition, 80, 'avgSirina', template);
      yPosition += 25;
    } else {
      // Fallback na stare fiksne vrednosti ako nema sirineRows
      const fallbackSectionHeight = 85; // 8 polja (3 reda) + srednja vrednost + header
      
      // Proveri da li cela sekcija stane na stranicu
      if (yPosition > pageHeight - fallbackSectionHeight - 20) {
        pdf.addPage();
        yPosition = 25;
      }
      yPosition = drawSectionTitle('IZMERENE VREDNOSTI ŠIRINE (mm)', 15, yPosition, pageWidth - 30);
      
      drawSimpleField('Širina na 200 mm:', formatNumber(measurement.sirina200), 20, yPosition, 45);
      drawSimpleField('Širina na 1200 mm:', formatNumber(measurement.sirina1200), 70, yPosition, 45);
      drawSimpleField('Širina na 2200 mm:', formatNumber(measurement.sirina2200), 120, yPosition, 45);
      yPosition += 12;
      
      drawSimpleField('Širina na 3200 mm:', formatNumber(measurement.sirina3200), 20, yPosition, 45);
      drawSimpleField('Širina na 4200 mm:', formatNumber(measurement.sirina4200), 70, yPosition, 45);
      drawSimpleField('Širina na 5200 mm:', formatNumber(measurement.sirina5200), 120, yPosition, 45);
      yPosition += 12;
      
      drawSimpleField('Širina na 6200 mm:', formatNumber(measurement.sirina6200), 20, yPosition, 45);
      drawSimpleField('Širina na 7200 mm:', formatNumber(measurement.sirina7200), 70, yPosition, 45);
      yPosition += 12;
      
      drawValidatedSimpleField('Srednja vrednost (mm):', formatNumber(measurement.avgSirina), 20, yPosition, 80, 'avgSirina', template);
      yPosition += 25;
    }

    // Distribucija vlakana - dinamički prikaz
    const distribucije = [];
    // Eksplicitno proveravamo svako polje distribucije
    if (measurement.distribucija1 !== undefined && measurement.distribucija1 !== null) {
      distribucije.push({ broj: 1, vrednost: measurement.distribucija1 });
    }
    if (measurement.distribucija2 !== undefined && measurement.distribucija2 !== null) {
      distribucije.push({ broj: 2, vrednost: measurement.distribucija2 });
    }
    if (measurement.distribucija3 !== undefined && measurement.distribucija3 !== null) {
      distribucije.push({ broj: 3, vrednost: measurement.distribucija3 });
    }
    if (measurement.distribucija4 !== undefined && measurement.distribucija4 !== null) {
      distribucije.push({ broj: 4, vrednost: measurement.distribucija4 });
    }
    if (measurement.distribucija5 !== undefined && measurement.distribucija5 !== null) {
      distribucije.push({ broj: 5, vrednost: measurement.distribucija5 });
    }
    if (measurement.distribucija6 !== undefined && measurement.distribucija6 !== null) {
      distribucije.push({ broj: 6, vrednost: measurement.distribucija6 });
    }
    
    if (distribucije.length > 0) {
      // Kalkuliši tačnu visinu na osnovu broja distribucija
      const rowsNeeded = Math.ceil(distribucije.length / 3); // 3 kolone po redu
      const fieldsHeight = rowsNeeded * 12; // Svaki red polja je 12px
      const avgFieldHeight = 25; // Prostor za srednju vrednost
      const sectionPadding = 20; // Header i padding
      const totalSectionHeight = fieldsHeight + avgFieldHeight + sectionPadding;
      
      // Proveri da li cela sekcija stane na stranicu
      if (yPosition > pageHeight - totalSectionHeight - 20) {
        pdf.addPage();
        yPosition = 25;
      }
      
      yPosition = drawSectionTitle('DISTRIBUCIJA VLAKANA', 15, yPosition, pageWidth - 30);
      
      // Prikaži sve distribucije dinamički
      distribucije.forEach((dist, index) => {
        const xPos = 20 + (index % 3) * 60; // 3 kolone
        if (index % 3 === 0 && index > 0) yPosition += 12; // Nova linija svake 3 polja
        
        drawSimpleField(`Distribucija ${dist.broj}:`, formatNumber(dist.vrednost), xPos, yPosition, 55);
      });
      
      if (distribucije.length > 0) yPosition += 12;
      drawValidatedSimpleField('Srednja vrednost (mm):', formatNumber(measurement.avgDistribucija), 20, yPosition, 100, 'avgDistribucija', template);
      yPosition += 20;
    }

    // Proračun debljine - uvek prikaži sekciju
    // Dinamički izračunaj optimalnu visinu sekcije
    let baseHeight = 30; // Osnovna visina
    
    if (measurement.rows && measurement.rows.length > 0) {
      const tableHeaderHeight = 14; // Header tabele 
      const maxRowsPerPage = Math.floor((pageHeight - yPosition - 100) / 6); // Koliko redova stane na stranicu
      const visibleRows = Math.min(measurement.rows.length, maxRowsPerPage); // Redovi koji će biti vidljivi
      const tableRowsHeight = visibleRows * 6;
      const tableMargin = 10;
      
      baseHeight += tableHeaderHeight + tableRowsHeight + tableMargin;
    }
    
    // Dodaj prostor za srednju debljinu
    const avgThicknessSpace = 25;
    const optimalSectionHeight = baseHeight + avgThicknessSpace;
    
    // Maksimalna visina sekcije na trenutnoj stranici
    const maxAvailableHeight = pageHeight - yPosition - 50;
    const finalSectionHeight = Math.min(optimalSectionHeight, maxAvailableHeight);
    
    // Ako tabela ima previše redova, idi na novu stranicu
    if (measurement.rows && measurement.rows.length > 0) {
      const minRequiredHeight = 80; // Minimalno potrebno za header + neki sadržaj
      if (finalSectionHeight < minRequiredHeight) {
        pdf.addPage();
        yPosition = 25;
        // Ponovo kalkuliši na novoj stranici
        const newMaxHeight = pageHeight - yPosition - 50;
        const newOptimalHeight = Math.min(optimalSectionHeight, newMaxHeight);
        yPosition = drawSectionTitle('PRORACUN DEBLJINE', 15, yPosition, pageWidth - 30);
      } else {
        yPosition = drawSectionTitle('PRORACUN DEBLJINE', 15, yPosition, pageWidth - 30);
      }
    } else {
      // Ako nema tabelu, koristi osnovnu visinu
      yPosition = drawSectionTitle('PRORACUN DEBLJINE', 15, yPosition, pageWidth - 30);
    }
      
      // drawSimpleField('Debljina 1:', formatNumber(measurement.debljina1), 20, yPosition, 50);
      // drawSimpleField('Pretvaranje:', formatNumber(measurement.pretvaranje), 80, yPosition, 50);
      // yPosition += 12;
      
      if (measurement.rows && measurement.rows.length > 0) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        // pdf.text('Tabela debljine:', 20, yPosition);
        // yPosition += 8;
        
        // Draw table headers
        pdf.setFontSize(7);
        drawBox(20, yPosition, 20, 6);
        pdf.text('Tip', 22, yPosition + 4);
        drawBox(40, yPosition, 30, 6);
        pdf.text('Tacka', 42, yPosition + 4);
        drawBox(70, yPosition, 25, 6);
        pdf.text('Dužina', 72, yPosition + 4);
        drawBox(95, yPosition, 25, 6);
        pdf.text('Širina', 97, yPosition + 4);
        drawBox(120, yPosition, 25, 6);
        pdf.text('Izmereno', 122, yPosition + 4);
        drawBox(145, yPosition, 25, 6);
        pdf.text('Subler', 147, yPosition + 4);
        yPosition += 6;
        
        // Draw table rows - prikaži sve redove sa page break logikom
        measurement.rows.forEach((row, index) => {
          // Proveri da li treba nova stranica (ostavi prostor za footer i sledeći sadržaj)
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = 25;
            
            // Ponovno nacrtaj header tabele na novoj stranici
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
            // pdf.text('Tabela debljine (nastavak):', 20, yPosition);
            yPosition += 8;
            
            // Draw table headers again
            pdf.setFontSize(7);
            drawBox(20, yPosition, 20, 6);
            pdf.text('Tip', 22, yPosition + 4);
            drawBox(40, yPosition, 30, 6);
            pdf.text('Tacka', 42, yPosition + 4);
            drawBox(70, yPosition, 25, 6);
            pdf.text('Dužina', 72, yPosition + 4);
            drawBox(95, yPosition, 25, 6);
            pdf.text('Širina', 97, yPosition + 4);
            drawBox(120, yPosition, 25, 6);
            pdf.text('Izmereno', 122, yPosition + 4);
            drawBox(145, yPosition, 25, 6);
            pdf.text('Subler', 147, yPosition + 4);
            yPosition += 6;
          }
          
          drawBox(20, yPosition, 20, 6);
          pdf.text(row.type, 22, yPosition + 4);
          drawBox(40, yPosition, 30, 6);
          pdf.text(row.label, 42, yPosition + 4);
          drawBox(70, yPosition, 25, 6);
          pdf.text(formatNumber(row.length), 72, yPosition + 4);
          drawBox(95, yPosition, 25, 6);
          pdf.text(formatNumber(row.width), 97, yPosition + 4);
          drawBox(120, yPosition, 25, 6);
          pdf.text(formatNumber(row.measured), 122, yPosition + 4);
          drawBox(145, yPosition, 25, 6);
          pdf.text(formatNumber(row.caliper), 147, yPosition + 4);
          yPosition += 6;
        });
        yPosition += 5;
      }
      
      drawValidatedSimpleField('Srednja debljina (mm):', formatNumber(measurement.avgThickness), 20, yPosition, 80, 'avgThickness', template);
      yPosition += 25;

    // Check if we need a new page before AFr sections
    if (yPosition > pageHeight - 120) {
      pdf.addPage();
      yPosition = 25;
    }

    // AFr uzorci - dinamički prikaz
    const afrUzorci = [];
    
    // Proveravamo svaki AFr uzorak
    if ((measurement.debljinaUzorak1 !== undefined && measurement.debljinaUzorak1 !== null) || 
        (measurement.deltaP1 !== undefined && measurement.deltaP1 !== null)) {
      afrUzorci.push({
        broj: 1,
        debljina: measurement.debljinaUzorak1,
        deltaP: measurement.deltaP1
      });
    }
    
    if ((measurement.debljinaUzorak2 !== undefined && measurement.debljinaUzorak2 !== null) || 
        (measurement.deltaP2 !== undefined && measurement.deltaP2 !== null)) {
      afrUzorci.push({
        broj: 2,
        debljina: measurement.debljinaUzorak2,
        deltaP: measurement.deltaP2
      });
    }
    
    if ((measurement.debljinaUzorak3 !== undefined && measurement.debljinaUzorak3 !== null) || 
        (measurement.deltaP3 !== undefined && measurement.deltaP3 !== null)) {
      afrUzorci.push({
        broj: 3,
        debljina: measurement.debljinaUzorak3,
        deltaP: measurement.deltaP3
      });
    }
    
    // Prikaži samo uzorke koji postoje
    afrUzorci.forEach(uzorak => {
      // Proveri da li cela AFr sekcija stane na stranicu
      if (yPosition > pageHeight - 65) {
        pdf.addPage();
        yPosition = 25;
      }
      yPosition = drawSectionTitle(`AFr - UZORAK ${uzorak.broj}`, 15, yPosition, pageWidth - 30);
      
      drawSimpleField(`Debljina uzorak ${uzorak.broj}:`, formatNumber(uzorak.debljina), 20, yPosition, 60);
      drawSimpleField('ΔP (Pa):', formatNumber(uzorak.deltaP), 90, yPosition, 50);
      yPosition += 12;
      
      // Calculate AFr - simplified calculation for display
      const afrValue = uzorak.deltaP && uzorak.debljina ? 
        (uzorak.deltaP * uzorak.debljina / 1000).toFixed(2) : '';
      drawValidatedSimpleField('Afr (kPa·s/m²):', afrValue, 20, yPosition, 60, 'avgAFr', template);
      yPosition += 20;
    });

    // AFr - SREDNJA VREDNOST - uvek prikaži sekciju
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - 55) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('AFr - SREDNJA VREDNOST', 15, yPosition, pageWidth - 30);
    
    drawValidatedSimpleField('AFr - SV (kPa·s/m²):', formatNumber(measurement.avgAFr), 20, yPosition, 80, 'avgAFr', template);
    yPosition += 25;

    // Check if we need a new page before Lambda sections
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = 25;
    }

    // λ Vrednost
    yPosition = drawSectionTitle('λ VREDNOST', 15, yPosition, pageWidth - 30);
    
    drawValidatedSimpleField('Lambda vrednost:', formatNumberOrEmpty(measurement.lambdaVrednost), 20, yPosition, 80, 'lambdaVrednost', template);
    yPosition += 25;

    // CS (10)
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - 55) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('CS (10)', 15, yPosition, pageWidth - 30);
    
    drawValidatedSimpleField('CS (10):', formatNumberOrEmpty(measurement.cs10), 20, yPosition, 80, 'cs10', template);
    yPosition += 25;

    // λ Gustinga - uvek prikaži sekciju
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('λ GUSTINGA', 15, yPosition, pageWidth - 30);
    
    drawSimpleField('λ Gustinga:', formatNumberOrEmpty(measurement.lambdaGustinga), 20, yPosition, 80);
    yPosition += 20;

    // Lambda 90/90 - uvek prikaži sekciju
    // Proveri da li cela sekcija stane na stranicu
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 25;
    }
    yPosition = drawSectionTitle('LAMBDA 90/90', 15, yPosition, pageWidth - 30);
    
    drawSimpleField('Lambda 90/90:', formatNumberOrEmpty(measurement.lambda9090), 20, yPosition, 80);
    yPosition += 20;

    // Check if we need a new page before Status section
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = 25;
    }

    // Status Section
    yPosition = drawSectionTitle('STATUS KONTROLE', 15, yPosition, pageWidth - 30);
    
    const status = measurement.verified ? 'VERIFIKOVANO' : measurement.notVerified ? 'ODBIJENO' : 'CEKA PREGLED';
    const statusColor = measurement.verified ? '#28a745' : measurement.notVerified ? '#dc3545' : '#ffc107';
    
    // Status box with color
    const statusRgb = hexToRgb(statusColor);
    pdf.setFillColor(statusRgb.r, statusRgb.g, statusRgb.b);
    pdf.rect(20, yPosition, 60, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(status, 50, yPosition + 5.5, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    
    yPosition += 12;
    
    if (measurement.verified && measurement.verifiedBy) {
      drawSimpleField('Verifikovao:', measurement.verifiedBy, 20, yPosition, 80);
    }
    if (measurement.notVerified && measurement.notVerifiedBy) {
      drawSimpleField('Odbacio:', measurement.notVerifiedBy, 20, yPosition, 80);
    }
    yPosition += 20;

    // Napomena Section
    if (measurement.napomena) {
      // Check if we need a new page for Napomena
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 25;
      }
      
      yPosition = drawSectionTitle('NAPOMENA', 15, yPosition, pageWidth - 30);
      
      pdf.setFontSize(9);
      const napomenaLines = pdf.splitTextToSize(measurement.napomena, pageWidth - 40);
      
      // Check if napomena text fits on current page
      const napomenaHeight = Math.max(20, napomenaLines.length * 5);
      if (yPosition + napomenaHeight > pageHeight - 20) {
        pdf.addPage();
        yPosition = 25;
        yPosition = drawSectionTitle('NAPOMENA', 15, yPosition, pageWidth - 30);
      }
      
      pdf.text(napomenaLines, 20, yPosition);
      yPosition += napomenaHeight;
    }



    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Generisano: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, 15, pageHeight - 10);
    pdf.text(`ID: ${measurement._id}`, pageWidth - 15, pageHeight - 10, { align: 'right' });

    return pdf.output('blob');
  };

  const handleSingleExport = async (measurement: ProductMeasurement) => {
    setIsExporting(true);
    try {
      const pdfBlob = await generateProductFormPDF(measurement);
      const fileName = `${measurement.productType}_${format(new Date(measurement.datumProizvodnje), 'yyyy-MM-dd')}_${measurement._id}.pdf`;
      saveAs(pdfBlob, fileName);
    } catch (error) {
      console.error('Greška pri eksportovanju:', error);
      alert('Greška pri eksportovanju PDF-a');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMultipleExport = async () => {
    if (selectedProductIds.length === 0) {
      alert('Molimo selektujte proizvode za eksport');
      return;
    }

    setIsExporting(true);
    try {
      const zip = new JSZip();
      const selectedMeasurements = measurements.filter(m => selectedProductIds.includes(m._id));

      for (const measurement of selectedMeasurements) {
        const pdfBlob = await generateProductFormPDF(measurement);
        const fileName = `${measurement.productType}_${format(new Date(measurement.datumProizvodnje), 'yyyy-MM-dd')}_${measurement._id}.pdf`;
        zip.file(fileName, pdfBlob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `proizvodi_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.zip`;
      saveAs(zipBlob, zipFileName);
      
      // Clear selection after export
      setSelectedProductIds([]);
    } catch (error) {
      console.error('Greška pri eksportovanju:', error);
      alert('Greška pri eksportovanju zip fajla');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedProductIds.length === filteredMeasurements.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(filteredMeasurements.map(m => m._id));
    }
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  return (
    <Container maxWidth="xl" sx={{ 
      py: isMobile ? 2 : 4,
      '@media (max-width: 480px)': {
        py: 1,
        px: 1
      }
    }}>
      <Box sx={{ 
        mb: isMobile ? 2 : 4,
        '@media (max-width: 480px)': {
          mb: 1.5
        }
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '1.5rem',
              lineHeight: 1.2
            }
          }}
        >
          Tehnička merenja proizvoda
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '0.875rem'
            }
          }}
        >
          Pregled svih unetih merenja i rezultata
        </Typography>
        <Typography 
          variant="subtitle2"
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '0.75rem'
            }
          }}
        >
          Uloga: {role}
        </Typography>
      </Box>
      <Paper sx={{ 
        p: isMobile ? 2 : 3, 
        mb: isMobile ? 2 : 3,
        '@media (max-width: 480px)': {
          p: 1,
          mb: 1.5
        }
      }}>
                  <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: isMobile ? 1.5 : 2,
            '@media (max-width: 480px)': {
              mb: 1,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterIcon sx={{ 
                mr: 1,
                '@media (max-width: 480px)': {
                  fontSize: '1.2rem'
                }
              }} />
              <Typography 
                variant="h6"
                sx={{
                  '@media (max-width: 480px)': {
                    fontSize: '1.1rem'
                  }
                }}
              >
                Filteri
              </Typography>
            </Box>
                      {(role === 'admin' || role === 'master_admin') && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={cleanupDuplicateSessions}
                startIcon={<DeleteSweepIcon />}
                sx={{
                  '@media (max-width: 480px)': {
                    fontSize: '0.75rem',
                    minHeight: 36,
                    px: 1
                  }
                }}
              >
                Očisti duplikate sesija
              </Button>
            )}
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
          gap: isMobile ? 1.5 : 2,
          alignItems: 'center',
          '@media (max-width: 480px)': {
            gap: 1,
            gridTemplateColumns: '1fr'
          }
        }}>
          <Box sx={{ gridColumn: { xs: '1', sm: '1', md: '1 / span 3' } }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Lambda grupe</InputLabel>
              <Select
                multiple
                value={selectedLambdaGroups}
                label="Lambda grupe"
                onChange={e => setSelectedLambdaGroups(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Box key={value} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 1, borderRadius: 1, fontSize: 13 }}>{value}</Box>
                    ))}
                  </Box>
                )}
              >
                {lambdaGroups.map((g) => (
                  <MenuItem key={g._id} value={g.name}>
                    <Checkbox checked={selectedLambdaGroups.indexOf(g.name) > -1} />
                    <ListItemText primary={g.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={filteredProductTypes}
              value={selectedProductTypes}
              onChange={(_, value) => setSelectedProductTypes(value)}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipovi proizvoda"
                  placeholder="Počni da kucaš..."
                />
              )}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: '1', sm: '2', md: '4' } }}>
            <TextField
              fullWidth
              type="date"
              label="Od datuma"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: '1', sm: '1', md: '5' } }}>
            <TextField
              fullWidth
              type="date"
              label="Do datuma"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
            />
          </Box>
          <Box sx={{ 
            gridColumn: { xs: '1', sm: '2', md: '6' },
            display: 'flex',
            gap: 1
          }}>
            <Button
              variant="contained"
              onClick={handleFilter}
              startIcon={<FilterIcon />}
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '0.875rem',
                  minHeight: 40,
                  px: 2
                }
              }}
            >
              Filtriraj
            </Button>
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '0.875rem',
                  minHeight: 40,
                  px: 2
                }
              }}
            >
              Očisti
            </Button>
          </Box>
          <Box sx={{ 
            gridColumn: { xs: '1', sm: '1', md: '7' },
            mt: { xs: 2, md: 0 }
          }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/new')}
              startIcon={<AddIcon />}
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '0.875rem',
                  minHeight: 40,
                  px: 2
                }
              }}
            >
              Novi unos
            </Button>
          </Box>
          {(role === 'admin' || role === 'master_admin') && (
            <Box sx={{ 
              gridColumn: { xs: '1', sm: '1', md: '8' },
              mt: { xs: 2, md: 0 }
            }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  // console.log('🔍 Manual refresh triggered by admin/master_admin');
                  loadMeasurements();
                  loadActiveSessions();
                }}
                startIcon={<RefreshIcon />}
                sx={{
                  '@media (max-width: 480px)': {
                    fontSize: '0.875rem',
                    minHeight: 40,
                    px: 2
                  }
                }}
              >
                Osveži sve
              </Button>
            </Box>
          )}
          <Box sx={{ 
            gridColumn: { xs: '1', sm: '1', md: '7' },
            mt: { xs: 2, md: 0 }
          }}>
            {(role === 'admin' || role === 'master_admin') && (
              <>
                <FormControlLabel
                  control={<Checkbox checked={excludeRejected} onChange={e => setExcludeRejected(e.target.checked)} />}
                  label="Isključi odbijene"
                  sx={{
                    '@media (max-width: 480px)': {
                      fontSize: '0.875rem',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem'
                      }
                    }
                  }}
                />
                <FormControlLabel
                  control={<Checkbox checked={excludeVerified} onChange={e => setExcludeVerified(e.target.checked)} />}
                  label="Isključi verifikovane"
                  sx={{
                    '@media (max-width: 480px)': {
                      fontSize: '0.875rem',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem'
                      }
                    }
                  }}
                />
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '@media (max-width: 480px)': {
            gap: 1,
            alignItems: 'stretch'
          }
        }}>
          <FormControl size="small" sx={{ 
            minWidth: { xs: '100%', sm: 160 },
            '@media (max-width: 480px)': {
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '8px 12px'
              }
            }
          }}>
            <InputLabel id="sort-by-label">Sortiraj po</InputLabel>
            <Select
              labelId="sort-by-label"
              value={sortBy}
              label="Sortiraj po"
              onChange={e => setSortBy(e.target.value)}
            >
              <MenuItem value="datumProizvodnje">Datum proizvodnje</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            sx={{
              '@media (max-width: 480px)': {
                fontSize: '0.875rem',
                minHeight: 40,
                px: 2,
                flex: 1
              }
            }}
          >
            {sortOrder === 'asc' ? 'Rastuće' : 'Opadajuće'}
          </Button>
        </Box>
      </Paper>
      
      {/* Export controls - samo za admin i master_admin */}
      {(role === 'admin' || role === 'master_admin') && !loading && filteredMeasurements.length > 0 && (
        <Paper sx={{ 
          p: 2, 
          mb: 2,
          bgcolor: 'grey.50'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">
                Export formulara:
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAll}
                disabled={isExporting}
                sx={{ minWidth: 'auto' }}
              >
                {selectedProductIds.length === filteredMeasurements.length ? 'Poništi sve' : 'Selektuj sve'}
              </Button>
              {selectedProductIds.length > 0 && (
                <Typography variant="caption" color="primary">
                  {selectedProductIds.length} selektovano
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleMultipleExport}
                disabled={isExporting || selectedProductIds.length === 0}
                startIcon={isExporting ? <CircularProgress size={16} /> : <ArchiveIcon />}
              >
                {isExporting ? 'Eksportuje...' : `Export ZIP ${selectedProductIds.length > 0 ? `(${selectedProductIds.length})` : ''}`}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Paper sx={{
          '@media (max-width: 480px)': {
            overflow: 'auto'
          }
        }}>
          <TableContainer sx={{
            '@media (max-width: 480px)': {
              maxWidth: '100vw',
              overflow: 'auto'
            }
          }}>
            <Table sx={{
              '@media (max-width: 480px)': {
                fontSize: '0.75rem',
                minWidth: 800 // Osiguraj da tabela može da se skroluje horizontalno
              }
            }}>
              <TableHead>
                <TableRow>
                  {/* Checkbox kolona - samo za admin i master_admin */}
                  {(role === 'admin' || role === 'master_admin') && (
                    <TableCell sx={{
                      '@media (max-width: 480px)': {
                        padding: '8px 4px',
                        fontSize: '0.75rem'
                      }
                    }}>
                      <Checkbox
                        indeterminate={selectedProductIds.length > 0 && selectedProductIds.length < filteredMeasurements.length}
                        checked={filteredMeasurements.length > 0 && selectedProductIds.length === filteredMeasurements.length}
                        onChange={handleSelectAll}
                        disabled={isExporting}
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Tip proizvoda</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Datum proizvodnje</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Vreme proizvodnje</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Merio</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>SM%</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Duzina (SV)</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Sirina (SV)</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Debljina (SV)</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Status</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Naknadno</TableCell>
                  <TableCell sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Kontrolisao</TableCell>
                  <TableCell align="right" sx={{
                    '@media (max-width: 480px)': {
                      padding: '8px 4px',
                      fontSize: '0.75rem'
                    }
                  }}>Akcije</TableCell>
                  {/* Export kolona - samo za admin i master_admin */}
                  {(role === 'admin' || role === 'master_admin') && (
                    <TableCell align="center" sx={{
                      '@media (max-width: 480px)': {
                        padding: '8px 4px',
                        fontSize: '0.75rem'
                      }
                    }}>Export</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMeasurements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={(role === 'admin' || role === 'master_admin') ? 13 : 11} align="center">
                      Nema pronađenih merenja
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMeasurements.map((measurement, idx) => (
                    <TableRow key={measurement._id}>
                      {/* Checkbox celija - samo za admin i master_admin */}
                      {(role === 'admin' || role === 'master_admin') && (
                        <TableCell sx={{
                          '@media (max-width: 480px)': {
                            padding: '8px 4px',
                            fontSize: '0.75rem'
                          }
                        }}>
                          <Checkbox
                            checked={selectedProductIds.includes(measurement._id)}
                            onChange={() => handleSelectProduct(measurement._id)}
                            disabled={isExporting}
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell
                        sx={{ 
                          borderLeft: 4, 
                          borderLeftColor: measurement.verified ? 'success.main' : measurement.notVerified ? 'error.main' : 'grey.400',
                          '@media (max-width: 480px)': {
                            padding: '8px 4px',
                            fontSize: '0.75rem'
                          }
                        }}
                      >
                        {measurement.productType}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {format(new Date(measurement.datumProizvodnje), 'dd.MM.yyyy')}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {measurement.vremeProizvodnje || ''}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {measurement.kontrolisao}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>{
                        typeof measurement.procenatSM === 'number' && !isNaN(measurement.procenatSM)
                          ? measurement.procenatSM.toFixed(2)
                          : ''
                      }</TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>{
                        measurement.avgDuzina && !isNaN(Number(measurement.avgDuzina))
                          ? Number(measurement.avgDuzina).toFixed(2)
                          : ''
                      }</TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>{
                        measurement.avgSirina && !isNaN(Number(measurement.avgSirina))
                          ? Number(measurement.avgSirina).toFixed(2)
                          : ''
                      }</TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>{
                        measurement.avgThickness && !isNaN(Number(measurement.avgThickness))
                          ? Number(measurement.avgThickness).toFixed(2)
                          : ''
                      }</TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {measurement.verified
                          ? 'Verifikovano'
                          : measurement.notVerified
                            ? 'Odbijeno'
                            : 'Čeka pregled'
                        }
                        {isMeasurementBeingEdited(measurement._id) && (
                          <Box component="span" sx={{ 
                            ml: 1, 
                            fontSize: '0.75rem', 
                            color: 'warning.main',
                            '@media (max-width: 480px)': {
                              fontSize: '0.625rem',
                              ml: 0.5
                            }
                          }}>
                            (U edit modu)
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {measurement.naknadno ? 'D' : ''}
                      </TableCell>
                      <TableCell sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {measurement.verified ? measurement.verifiedBy : measurement.notVerified ? measurement.notVerifiedBy : ''}
                      </TableCell>
                      <TableCell align="right" sx={{
                        '@media (max-width: 480px)': {
                          padding: '8px 4px',
                          fontSize: '0.75rem'
                        }
                      }}>
                        {/* Ako je tehnolog i status je zakljucan, prikaži katanac umesto edit dugmeta */}
                        {role === 'tehnolog' && (measurement.verified || measurement.notVerified) ? (
                          <Tooltip title="Zaključano">
                            <span>
                              <IconButton 
                                size="small" 
                                disabled
                                sx={{
                                  '@media (max-width: 480px)': {
                                    padding: '4px',
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '1rem'
                                    }
                                  }
                                }}
                              >
                                <LockIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : role === 'tehnolog' && measurement.kontrolisao !== currentUser.username ? (
                          <Tooltip title="Možete uređivati samo svoje unose">
                            <span>
                              <IconButton 
                                size="small" 
                                disabled
                                sx={{
                                  '@media (max-width: 480px)': {
                                    padding: '4px',
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '1rem'
                                    }
                                  }
                                }}
                              >
                                <LockIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : !isMeasurementBeingEdited(measurement._id) ? (
                          <Tooltip title="Izmeni">
                            <IconButton 
                              size="small" 
                              onClick={() => navigate(`/edit/${measurement._id}`)} 
                              disabled={role === 'direktor'}
                              sx={{
                                '@media (max-width: 480px)': {
                                  padding: '4px',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem'
                                  }
                                }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {(role === 'admin' || role === 'master_admin') && (
                          <Tooltip title="Kontroliši">
                            <IconButton 
                              size="small" 
                              color="success" 
                              onClick={() => navigate(`/edit/${measurement._id}?kontrola=1`)}
                              sx={{
                                '@media (max-width: 480px)': {
                                  padding: '4px',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem'
                                  }
                                }
                              }}
                            >
                              <KontrolisiIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(role === 'admin' || role === 'master_admin') && (
                          <Tooltip title="Obriši">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDelete(measurement._id)}
                              sx={{
                                '@media (max-width: 480px)': {
                                  padding: '4px',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem'
                                  }
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Dodaj dugme za dupliranje samo za odbijene i samo za admina/master_admin */}
                        {(measurement.notVerified && (role === 'admin' || role === 'master_admin')) && (
                          <Tooltip title="Dupliraj">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleDuplicate(measurement)}
                              sx={{
                                '@media (max-width: 480px)': {
                                  padding: '4px',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem'
                                  }
                                }
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Take Over dugme za admin/master_admin kada je merenje u edit modu */}
                        {((role === 'admin' || role === 'master_admin') && (isMeasurementBeingEdited(measurement._id) || isCurrentUserEditingMeasurement(measurement._id))) && (() => {
                          const session = getActiveSession(measurement._id);
                          const sessionAge = session ? Date.now() - new Date(session.lastActivity).getTime() : 0;
                          const isSessionActive = sessionAge <= 5 * 60 * 1000; // 5 minuta
                          
                          // Proveri da li je trenutni korisnik već vlasnik sesije
                          const isCurrentUserOwner = session && session.username === currentUser.username;
                          
                          // Proveri da li postoje duplikati sesija za ovaj measurement
                          const allSessionsForMeasurement = activeSessions.filter(s => 
                            s.resourceId === measurement._id && 
                            s.resourceType === 'measurement'
                          );
                          const hasDuplicates = allSessionsForMeasurement.length > 1;
                          
                          // Prikaži "Take Over" dugme samo ako trenutni korisnik NIJE vlasnik sesije
                          if (isSessionActive && !isCurrentUserOwner) {
                            return (
                              <Tooltip title={`Preuzmi kontrolu od ${session?.username} i nastavi editovanje${hasDuplicates ? ' (postoje duplikati sesija)' : ''}`}>
                                <IconButton 
                                  size="small" 
                                  sx={{ 
                                    color: hasDuplicates ? 'error.main' : 'warning.main',
                                    backgroundColor: hasDuplicates ? 'error.light' : 'warning.light',
                                    '@media (max-width: 480px)': {
                                      padding: '4px',
                                      '& .MuiSvgIcon-root': {
                                        fontSize: '1rem'
                                      }
                                    },
                                    '&:hover': {
                                      backgroundColor: hasDuplicates ? 'error.main' : 'warning.main',
                                      color: 'white'
                                    }
                                  }}
                                  onClick={() => {
                                    // console.log('🔍 Take Over button clicked for measurement:', measurement._id);
                                    // console.log('🔍 Session found for take over:', session);
                                    if (session?.sessionId) {
                                      // console.log('🔍 Calling handleTakeOver with sessionId:', session.sessionId);
                                      handleTakeOver(session.sessionId);
                                    } else {
                                      // console.error('🔍 No sessionId found for take over');
                                      alert('Greška: Session ID nije dostupan za take over');
                                    }
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            );
                          } else if (isSessionActive && isCurrentUserOwner) {
                            // Trenutni korisnik je vlasnik sesije - prikaži indikator da je u edit modu
                            return (
                              <Tooltip title={`Vi trenutno editujete ovo merenje${hasDuplicates ? ' (postoje duplikati sesija)' : ''}`}>
                                <IconButton 
                                  size="small" 
                                  sx={{ 
                                    color: hasDuplicates ? 'error.main' : 'success.main',
                                    backgroundColor: hasDuplicates ? 'error.light' : 'success.light',
                                    '&:hover': {
                                      backgroundColor: hasDuplicates ? 'error.main' : 'success.main',
                                      color: 'white'
                                    }
                                  }}
                                  onClick={() => {
                                    // console.log('🔍 User is already editing this measurement, navigating to edit page');
                                    navigate(`/edit/${measurement._id}`);
                                  }}
                                >
                                  <LockIcon />
                                </IconButton>
                              </Tooltip>
                            );
                          }
                          return null;
                        })()}
                      </TableCell>
                      {/* Export dugme - samo za admin i master_admin */}
                      {(role === 'admin' || role === 'master_admin') && (
                        <TableCell align="center" sx={{
                          '@media (max-width: 480px)': {
                            padding: '8px 4px',
                            fontSize: '0.75rem'
                          }
                        }}>
                          <Tooltip title="Export PDF forma">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleSingleExport(measurement)}
                              disabled={isExporting}
                              sx={{
                                '@media (max-width: 480px)': {
                                  padding: '4px',
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem'
                                  }
                                }
                              }}
                            >
                              {isExporting ? <CircularProgress size={16} /> : <PdfIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredMeasurements.length > 10 && (
            <TablePagination
              component="div"
              count={filteredMeasurements.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Prikaz po strani"
            />
          )}
        </Paper>
      )}
    </Container>
  );
};

export default Dashboard; 